import {
  Typography, AppBar, ListItemIcon, ListItemText, MenuItem,
} from '@mui/material';
import { useRefresh, useRedirect, useTranslate } from 'ra-core';
import {
  UserMenu, Logout, useNotify,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import {
  useEffect, useState,
} from 'react';
import * as packageDetails from '../../../package.json';
import { CustomLocaleMenuButton } from '../../components/locales/CustomLocaleMenuButton';
import { CustomLoadingIndicator } from '../../components/CustomLoadingIndicator';
import authProvider, {
  getGlobalRolesFromStorage,
  userHasRelationToCustomerId,
} from '../../utils/authProvider';
import { MyUserSettings } from './MyUserSettings';
import ReactSelect from '../../lib/react-select/reactSelect';
import { BUILD_COMMIT_SHA, BUILD_REF_NAME } from '../../config';
import { CustomerAccessRequestNotificationsBootstrap } from './components/CustomerAccessRequestNotificationsBootstrap';
import { notifyCurrentCustomerChanged } from '../../lib/currentCustomer';

export const MyAppBar = (props: any) => {

  const location = useLocation();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const translate = useTranslate();
  const notify = useNotify();

  const isUUID = (string: string) => {

    return string.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);

  };

  const [customers, setCustomers] = useState<{ value: string; label: string }[]>(() => {

    try {

      return JSON.parse(localStorage.getItem('customers') || '[]');

    } catch {

      return [];

    }

  });

  const [selectedCustomer, setSelectedCustomer] = useState<{ value: string; label: string } | null>(null);

  useEffect(() => {

    const syncFromStorage = () => {

      try {

        const list = JSON.parse(localStorage.getItem('customers') || '[]') as { value: string; label: string }[];
        const raw = sessionStorage.getItem('currentCustomer') || localStorage.getItem('lastSavedCustomer');
        if (raw) {

          const parsed = JSON.parse(raw) as { value: string; label: string };
          if (parsed?.value) {

            const canonical = list.find((c) => c.value === parsed.value);
            if (canonical) {

              setCustomers(list);
              setSelectedCustomer(canonical);
              return;

            }
            // Keep a stored customer that is not in the cached list yet (e.g. just created,
            // before refreshContext replaces `customers`). Do not persist list[0] over it.
            setCustomers([parsed, ...list]);
            setSelectedCustomer(parsed);
            return;

          }

        }
        setCustomers(list);
        if (list[0]) {

          const json = JSON.stringify(list[0]);
          sessionStorage.setItem('currentCustomer', json);
          localStorage.setItem('lastSavedCustomer', json);
          notifyCurrentCustomerChanged();
          setSelectedCustomer(list[0]);

        } else {

          setSelectedCustomer(null);

        }

      } catch {

        setCustomers([]);
        setSelectedCustomer(null);

      }

    };
    syncFromStorage();
    window.addEventListener('contextRefreshed', syncFromStorage);
    return () => window.removeEventListener('contextRefreshed', syncFromStorage);

  }, []);

  const changeCustomer = async (event: any) => {

    const globalRoles = getGlobalRolesFromStorage();
    const isGlobalSalesOrSupport = globalRoles.includes('sales_global') || globalRoles.includes('support_global');
    if (isGlobalSalesOrSupport && event?.value && !userHasRelationToCustomerId(event.value)) {

      localStorage.setItem('lastSavedCustomer', JSON.stringify(event));
      sessionStorage.setItem('currentCustomer', JSON.stringify(event));
      notifyCurrentCustomerChanged();
      setSelectedCustomer(event);
      notify('resources.customer_access_request.text.redirected_from_customer_switch', { type: 'info' });
      redirect('/customer-access-requests/new');
      return;

    }

    const url = window.location.pathname.split('/');
    let lastPart = url.pop();
    localStorage.setItem('lastSavedCustomer', JSON.stringify(event));
    sessionStorage.setItem('currentCustomer', JSON.stringify(event));
    // Selection clear is handled by useAutoUnselector on CURRENT_CUSTOMER_CHANGED_EVENT
    // (path-based useUnselectAll misses lists whose resource ≠ URL, e.g. all-assets).
    notifyCurrentCustomerChanged();
    setSelectedCustomer(event);

    if (!lastPart) {

      lastPart = '';

    }

    if (isUUID(lastPart)) {

      redirect(url.join('/'));
      return;

    }
    refresh();
    if (lastPart === 'live-map') {

      window.location.reload();

    }

    if (url.pop() === 'reports') {

      window.location.reload();

    }

  };

  const toggleCustomerAdminSimulation = () => {

    const isSimulating = sessionStorage.getItem('simulateCustomerAdmin');
    if (isSimulating) {

      sessionStorage.removeItem('simulateCustomerAdmin');

    } else {

      sessionStorage.setItem('simulateCustomerAdmin', 'true');

    }
    window.location.reload();

  };

  const defaultCustomer = () => {

    const sessionCustomer = sessionStorage.getItem('currentCustomer');
    const localCustomer = localStorage.getItem('lastSavedCustomer');

    const currentCustomer = sessionCustomer || localCustomer;
    if (!sessionCustomer && localCustomer) {

      sessionStorage.setItem('currentCustomer', localCustomer);

    }
    if (!currentCustomer) {

      if (!customers.length) {

        return {};

      }
      sessionStorage.setItem('currentCustomer', JSON.stringify(customers[0]));
      return customers[0];

    }

    return JSON.parse(currentCustomer);

  };

  const redirectWithoutCustomerParam = () => {

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('customer');
    redirect(newUrl.pathname + newUrl.search);

  };

  useEffect(() => {

    if (location.search) {

      const url = new URLSearchParams(location.search);
      const customer = url.get('customer');
      if (customer) {

        const customerFromQuery = customers.find((c: any) => c.value === customer);
        if (customerFromQuery) {

          changeCustomer(customerFromQuery).then(() => {

            redirectWithoutCustomerParam();
            window.location.reload();

          });

        } else {

          notify('general.text.customer_not_found', { type: 'warning' });
          redirectWithoutCustomerParam();

        }

      }

    }

  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppBar
      elevation={0}
      sx={{
        '& .RaAppBar-title': {
          flex: 1,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
        padding: '5px 15px',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
      color="secondary"
      {...props}
      title={null}
    >
      <CustomerAccessRequestNotificationsBootstrap/>
      <div style={{ display: 'flex', flexDirection: 'row', height: 38 }}>
        <img src="/assets/img/logo.svg" height="35" alt="X-Guard" style={{ verticalAlign: 'middle' }}/>
        <Typography
          variant="h6"
          color="inherit"
          sx={{
            display: 'inline-block',
            lineHeight: '35px',
            position: 'relative',
            top: '4px',
            paddingLeft: '15px',
          }}
        >Management Dashboard</Typography>
        <Typography
          variant="subtitle2"
          color="#EFEFEF"
          sx={{ display: 'inline-block', lineHeight: '47px', paddingLeft: '5px' }}
        >
          {BUILD_REF_NAME === 'develop'
            ? <a target={'_blank'} href={`https://github.com/X-Guard/xgac-r-management-dashboard/commit/${BUILD_COMMIT_SHA}`} rel="noreferrer" style={{ color: 'white' }}>{`${BUILD_COMMIT_SHA}`.substring(0, 7)}</a>
            : packageDetails.version}
        </Typography>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', marginTop: '4px' }}>
        <Typography variant="h6" id="react-admin-title" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', height: 38 }}>
        <div style={{ width: 300, marginRight: '1em', marginTop: 4 }}>
          {customers.length > 1
            && <ReactSelect
              onChange={changeCustomer}
              options={customers}
              value={selectedCustomer ?? defaultCustomer()}
              isDisabled={customers.length <= 1}
            />
          }
        </div>
        <UserMenu {...props}>
          {(authProvider.isAdminSimulation()
            && <>
              <MyUserSettings/>
              <MenuItem sx={{ color: 'text.secondary' }} onClick={toggleCustomerAdminSimulation}>
                <ListItemIcon>
                  <LockIcon/>
                </ListItemIcon>
                <ListItemText>{translate(authProvider.isAdmin()
                  ? 'general.text.simulate_customer_admin'
                  : 'general.text.exit_role_simulation')}
                </ListItemText>
              </MenuItem>
            </>)}
          <Logout/>
        </UserMenu>
        <CustomLocaleMenuButton/>
        <CustomLoadingIndicator/>
      </div>
    </AppBar>
  );

};
